// src/components/providerAssignments/MemberProviderChips.tsx
//
// Phase 12.7 — read-only row of provider chips shown per Central Team
// member on the Circle → People tab. Renders one chip per assigned
// channel; renders nothing when the member has no pins (the Manage
// button remains the entry point for Heads).
//
// Displays only safe display fields (name + status). NEVER exposes
// credentials, senderIdentity, or provider vendor secrets.

import type {
  MemberAssignmentChannel,
  MemberProviderAssignment,
} from '../../api/circlesApi';

const CHANNEL_LABELS: Record<MemberAssignmentChannel, string> = {
  voice: 'Voice',
  sms: 'SMS',
  email: 'Email',
  whatsapp: 'WhatsApp',
  voice_ai: 'Voice AI',
};

export interface MemberProviderChipsProps {
  memberUserId: string;
  assignments: MemberProviderAssignment[];
}

export default function MemberProviderChips({
  memberUserId,
  assignments,
}: MemberProviderChipsProps) {
  if (!assignments || assignments.length === 0) return null;

  // Stable order: voice, sms, email, whatsapp, voice_ai.
  const order: MemberAssignmentChannel[] = [
    'voice',
    'sms',
    'email',
    'whatsapp',
    'voice_ai',
  ];
  const sorted = [...assignments].sort(
    (a, b) => order.indexOf(a.channelType) - order.indexOf(b.channelType),
  );

  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-1.5"
      data-testid={`circle-member-provider-chips-${memberUserId}`}
    >
      {sorted.map((a) => {
        const label =
          a.providerDisplayName || a.providerAppKey || a.outboundProviderId;
        const suspended =
          typeof a.providerStatus === 'string' &&
          a.providerStatus !== 'active' &&
          a.providerStatus !== 'connected';
        return (
          <span
            key={`${memberUserId}-${a.channelType}-${a.outboundProviderId}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
              suspended
                ? 'bg-slate-50 text-slate-500 ring-slate-200'
                : 'bg-indigo-50 text-indigo-700 ring-indigo-200'
            }`}
            data-testid={`circle-member-provider-chip-${memberUserId}-${a.channelType}`}
            title={
              suspended
                ? `${CHANNEL_LABELS[a.channelType]}: ${label} (inactive — routing falls back)`
                : `${CHANNEL_LABELS[a.channelType]}: ${label}`
            }
          >
            <span className="uppercase tracking-wide text-[9px] text-slate-500">
              {CHANNEL_LABELS[a.channelType]}
            </span>
            <span className="truncate max-w-[10rem]">{label}</span>
            {suspended && <span aria-hidden>·</span>}
            {suspended && (
              <span className="text-[9px] uppercase tracking-wide">
                inactive
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
